import {useCallback, useEffect, useState} from 'react';
import {StudyContent} from '@cp/model';
import stripMarkdownFences from '../../../utils/string/stripMarkdownFences';
import joinStudyContentToContent from './utils/joinStudyContentToContent';
import splitContentToStudyContent from './utils/splitContentToStudyContent';

export default function useAdminStudyPageContentStudyContent() {
  const [content, setContentState] = useState<string>('');
  const [studyContent, setStudyContentState] = useState<StudyContent[]>([]);
  const [pageMode, setPageModeState] = useState<boolean>(true);

  // setContent: sets content and clears studyContent
  const setContent = useCallback((newContent: string) => {
    const strippedContent = stripMarkdownFences(newContent);
    setContentState(strippedContent);
    setStudyContentState([]);
  }, []);

  // setStudyContent: sets studyContent and clears content
  const setStudyContent = useCallback((newStudyContent: StudyContent[]) => {
    setStudyContentState(newStudyContent);
    setContentState('');
  }, []);

  // updateStudyContent: updates a specific item in studyContent array
  const updateStudyContent = useCallback((index: number, updatedContent: StudyContent) => {
    setStudyContentState((prev) => {
      const updated = [...prev];
      if (index >= 0 && index < updated.length) {
        updated[index] = updatedContent;
      }
      return updated;
    });
  }, []);

  // Handle conversion based on pageMode and current state
  useEffect(() => {
    if (pageMode) {
      // In page mode: convert content to studyContent if content exists
      if (content.length && studyContent.length === 0) {
        const sections = splitContentToStudyContent(content);
        setStudyContent(sections);
      }
    } else {
      // In normal mode: convert studyContent to content if studyContent exists
      if (studyContent.length > 0 && !content) {
        const joinedContent = joinStudyContentToContent(studyContent);
        setContent(joinedContent);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageMode]);

  // setPageMode wrapper
  const setPageMode = useCallback((newPageMode: boolean) => {
    setPageModeState(newPageMode);
  }, []);

  const getData = useCallback(() => {
    if (pageMode) {
      return joinStudyContentToContent(studyContent);
    }
    return content;
  }, [pageMode, studyContent, content]);

  const setData = useCallback(
    (newData: string | StudyContent[]) => {
      if (pageMode) {
        setStudyContent(Array.isArray(newData) ? newData : splitContentToStudyContent(newData));
      } else {
        const contentToSet = typeof newData === 'string' ? newData : joinStudyContentToContent(newData);
        setContent(contentToSet);
      }
    },
    [pageMode, setStudyContent, setContent],
  );

  return {
    content,
    setContent,
    studyContent,
    updateStudyContent,
    setStudyContent,
    pageMode,
    setPageMode,
    getData,
    setData,
  };
}
